import { LinkedClinic, LinkedUser, PatientCategory, Sample } from 'models/order';
import { Organization } from 'models/organization';
import { User } from 'models/user';
import { File as DenthubFile } from 'models/file';

export type AllAdditionalInfo = {
    additionalInfo: string;
    createdAt: Date;
    updatedBy: string;
    version: number;
};

export type DeliveryDetails = {
    time?: {
        hour?: number;
        minute?: number;
    };
    date: Date;
    sample: Sample;
    internalDeliveryDate?: Date | null;
    externalDeliveryDate?: Date | null;
};

export type OrderInputBase = {
    _id?: string;
    inputType: OrderInputType;
    patient?: PatientFormData;
    /** DD-3473: the ordering organization (formerly `clinic`); a lab for supplier orders. */
    customer?: Partial<Organization>;
    dentist?: Partial<User>;
    comment: string;
    historicalComments: AllAdditionalInfo[];
    isWarrantyOrder?: boolean;
    files: DenthubFile[];
    sample: Sample;
    labExceptions?: Record<string, boolean>;
    deliveryDetails: {
        date: Date | null;
        time: DeliveryTime;
        isPickUpAtClinic?: boolean;
        pickUpLocation?: Organization;
        destination?: Organization;
    };
};

export type PatientFormData = {
    personalIdentityNumber: string;
    firstName: string;
    lastName: string;
    noPersonalIdentityNumber: boolean;
    patientCategory?: PatientCategory;
};

export type Participants = {
    linkedDentists: LinkedUser[];
    linkedClinics: LinkedClinic[];
    customParticipantEmails: string[];
};

export enum OrderInputType {
    NEW = 'NEW',
    MODIFY = 'MODIFY',
    COPY = 'COPY',
    WARRANTY = 'WARRANTY',
}

export type DeliveryTime = {
    hour: number | undefined;
    minute: number | undefined;
};

export type MaterialGridInput = {
    /** The catalogue material (price list) id; empty for a free-text row. */
    _id: string;
    /** The stored restoration material row this grid row edits, when it exists already. */
    rowId?: string;
    materialCode: string;
    description: string;
    batchNumber: string;
    quantity: string;
    basePrice: number;
    unitPrice: string;
    vatPercent: string;
    priceWithoutVat: number;
    priceWithVat: number;
    isValid: boolean;
    isActionMaterial: boolean;
    actionId?: string;
    originalPrice: number;
    originalVat: number;
};

export type ActionGridInput = {
    _id: string;
    /** The stored restoration action row this grid row edits, when it exists already. */
    rowId?: string;
    actionId: string;
    actionCode: string;
    description: string;
    quantity: string;
    basePrice: number;
    unitPrice: string;
    tiotandvardPrice: string;
    discountedPrice?: number;
    customerSpecificTiotandvardPrice?: number;
    price?: string;
    vatPercent: string;
    priceWithoutVat: number;
    priceWithVat: number;
    category: string;
    isValid: boolean;
    originalPrice: number;
    originalVat: number;
    quotable?: boolean;
    /** Priced per tooth: the quantity follows the teeth and the server recounts it on save. */
    costPerTooth?: boolean;
};

export enum ConstructionPropertyType {
    SHADE = 'SHADE',
    MATERIAL = 'MATERIAL',
    BITETRAY_MATERIAL = 'BITETRAY_MATERIAL',
    CLASP_MATERIAL = 'CLASP_MATERIAL',
    ORTHODONTICS_COLORS = 'ORTH_COLOR',
    FACIAL_LOOP = 'FACIAL_LOOP',
    CLASP_TYPES = 'CLASP_TYPE',
    SPRING_TYPES = 'SPRING_TYPE',
    METAL_DESIGN = 'DESIGN',
    TESTING_STEPS = 'TESTING_STEPS',
    IMPLANT_STEPS = 'IMPLANT_STEPS',
    DENTURE_STEPS = 'DENTURE_STEPS',
    PONTIC_DESIGN = 'PONTIC_DESIGN',
    OCCLUSION_TYPE = 'OCCLUSION',
    CONTACT_TYPE = 'CONTACT_TYPE',
    RETAINMENT = 'RETAINMENT',
    CLASP_TEETH = 'CLASP_TEETH',
    SPRING_TEETH = 'SPRING_TEETH',
    EXPANSION_SCREW = 'EXPANSION_SCREW',
    PROTRUSION = 'PROTRUSION',
}
